import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import aviso from '../lib/aviso';
import FormDialog from './FormDialog';
import Select from './Select';
import { canAccessRoute, getUserPermissions } from '../config/permissions';
import { MotivoPerdaService, type MotivoPerda } from '../services/MotivoPerdaService';

interface LossReasonModalProps {
    isOpen: boolean;
    dealTitle: string;
    onConfirm: (motivoPerdaId: number, motivoPerdaDetalhe: string) => Promise<void>;
    onCancel: () => void;
}

/**
 * Marcar uma oportunidade como perdida, dizendo por quê.
 *
 * Redesenhado em 2026-10-11 sobre o `FormDialog` — era um dos três modais
 * "feitos à mão" que o inventário de 27/09 listou como pendentes (os outros:
 * vincular produto e temperatura). Prefixo `lrm-` aposentado junto com o CSS.
 *
 * **O que saiu, e por quê:**
 *
 * - **A faixa vermelha** *"Para marcar esta oportunidade como venda perdida é
 *   necessário informar o motivo"*. Ela pintava de erro uma tela onde nada
 *   falhou — exatamente o vício que a proposta das mensagens do sistema
 *   diagnosticou (350 `toast.error` contra 160 de sucesso). O obrigatório já
 *   está dito pelo `*` no rótulo e pelo botão travado; o resto virou a linha
 *   de apoio do cabeçalho.
 * - **O ícone de alerta em triângulo.** Mesmo caso do círculo vermelho que o
 *   `Dialog` tirou: ornamento de severidade numa peça que não é um alarme.
 * - **O título "Motivo da Perda" com o nome do negócio embaixo, em cinza.**
 *   O nome do que está em jogo entrou na própria pergunta, que é a regra do
 *   `Dialog` — quem lê uma linha só já sabe qual oportunidade vai fechar.
 *
 * **O `CustomSelect` virou o `Select` do kit, com `portal`.** Não é troca
 * cosmética: o corpo do diálogo rola (`overflow-y: auto`), e lista de select
 * dentro de caixa que corta aparece recortada — foi o que aconteceu em
 * `/tarefas` e no roteamento do Agente. Lista cortada parece travada.
 */
const LossReasonModal = ({ isOpen, dealTitle, onConfirm, onCancel }: LossReasonModalProps) => {
    const navigate = useNavigate();
    const [motivos, setMotivos] = useState<MotivoPerda[]>([]);
    const [isLoadingMotivos, setIsLoadingMotivos] = useState(false);
    const [selectedMotivoId, setSelectedMotivoId] = useState<string>('');
    const [detalhe, setDetalhe] = useState('');
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (!isOpen) return;
        setSelectedMotivoId('');
        setDetalhe('');
        setIsSaving(false);

        const loadMotivos = async () => {
            setIsLoadingMotivos(true);
            try {
                const res = await MotivoPerdaService.getAll();
                if (res.sucesso) setMotivos(res.dados ?? []);
            } catch {
                aviso.erro('Não foi possível carregar os motivos de perda.');
            } finally {
                setIsLoadingMotivos(false);
            }
        };
        loadMotivos();
    }, [isOpen]);

    /* Sem motivo nenhum cadastrado o diálogo é um beco: o botão nunca destrava.
       A saída é a porta explícita para o cadastro — e só para quem a alcança. */
    const podeCadastrar = useMemo(
        () => (isOpen ? canAccessRoute('/motivos-perda', getUserPermissions()) : false),
        [isOpen],
    );
    const semMotivos = !isLoadingMotivos && motivos.length === 0;

    const handleConfirm = async () => {
        if (!selectedMotivoId) return;
        setIsSaving(true);
        try {
            await onConfirm(Number(selectedMotivoId), detalhe.trim());
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <FormDialog
            open={isOpen}
            onClose={onCancel}
            titulo={`Marcar «${dealTitle}» como perdida?`}
            sub="O motivo fica registrado na oportunidade."
            largura={440}
            confirmLabel="Confirmar perda"
            busyLabel="Confirmando…"
            confirmDisabled={!selectedMotivoId}
            busy={isSaving}
            onConfirm={handleConfirm}
        >
            <div className="ds-fd-campo">
                <label className="ds-fd-lab" htmlFor="lrm-motivo">
                    Motivo da perda <i aria-hidden="true">*</i>
                </label>
                <Select
                    id="lrm-motivo"
                    size="sm"
                    portal
                    value={selectedMotivoId}
                    onChange={setSelectedMotivoId}
                    disabled={isSaving || isLoadingMotivos || semMotivos}
                    placeholder={
                        isLoadingMotivos ? 'Carregando motivos…'
                            : semMotivos ? 'Nenhum motivo cadastrado'
                                : 'Selecione um motivo…'
                    }
                    options={motivos.map(m => ({
                        value: m.motivoPerdaId.toString(),
                        label: m.descricao,
                    }))}
                />
                {semMotivos && (
                    <p className="ds-fd-hint">
                        {podeCadastrar
                            ? 'Cadastre os motivos de perda para registrar por que as vendas não fecham.'
                            : 'Peça a um administrador para cadastrar os motivos de perda.'}
                    </p>
                )}
            </div>

            <div className="ds-fd-campo">
                <label className="ds-fd-lab" htmlFor="lrm-detalhe">Detalhe (opcional)</label>
                <textarea
                    id="lrm-detalhe"
                    className="ds-fd-ta"
                    value={detalhe}
                    onChange={e => setDetalhe(e.target.value)}
                    placeholder="Ex.: cliente achou caro comparado ao concorrente X"
                    rows={3}
                    disabled={isSaving}
                />
            </div>

            {semMotivos && podeCadastrar && (
                <button
                    type="button"
                    className="ds-fd-btn"
                    onClick={() => { onCancel(); navigate('/motivos-perda'); }}
                    disabled={isSaving}
                >
                    Cadastrar motivos de perda
                </button>
            )}
        </FormDialog>
    );
};

export default LossReasonModal;
